import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import SOSButton from '../components/SOSButton';
import SystemAnnouncementManager from '../components/SystemAnnouncementManager';
import AdminInvitationManager from '../components/AdminInvitationManager';
import { Badge, Button, Card, Input } from '../components/ui';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [logAction, setLogAction] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [logLimit, setLogLimit] = useState('50');
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [createAccountType, setCreateAccountType] = useState('client');
  const [currentView, setCurrentView] = useState('users');
  // Email compose state
  const [emailMode, setEmailMode] = useState('mass'); // 'mass' or 'individual'
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailTargetRole, setEmailTargetRole] = useState('all');
  const [emailTargetPhase, setEmailTargetPhase] = useState('all');
  const [emailTargetStatus, setEmailTargetStatus] = useState('active');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewing, setEmailPreviewing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUsers = async (params = {}) => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await adminService.getUsers(params);
      setUsers(response.data.data.users || []);
      setSelectedIds([]);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load users.'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (params = {}) => {
    setLogLoading(true);
    try {
      const response = await adminService.getAuditLogs(params);
      setLogs(response.data.data.logs || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load audit logs.'
      });
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadLogs({ limit: 50 });
  }, []);

  const filteredParams = useMemo(() => {
    const params = {};
    if (search) params.search = search;
    if (role) params.role = role;
    if (status) params.status = status;
    if (status === 'deleted' || search) params.includeDeleted = true;
    return params;
  }, [search, role, status]);

  const handleFilter = (e) => {
    e.preventDefault();
    loadUsers(filteredParams);
  };

  const toggleSelectLog = (logId, checked) => {
    setSelectedLogIds((prev) => {
      if (checked) {
        return [...new Set([...prev, logId])];
      }
      return prev.filter((id) => id !== logId);
    });
  };

  const toggleSelectAllLogs = (checked) => {
    if (checked) {
      setSelectedLogIds(logs.map((log) => log._id));
    } else {
      setSelectedLogIds([]);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Delete this audit log?')) {
      return;
    }

    setMessage(null);
    try {
      await adminService.deleteAuditLog(logId);
      await loadLogs({
        action: logAction || undefined,
        status: logStatus || undefined,
        limit: logLimit || 50
      });
      setMessage({ type: 'success', text: 'Audit log deleted.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete audit log.'
      });
    }
  };

  const handleDeleteSelectedLogs = async () => {
    if (selectedLogIds.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one log to delete.' });
      return;
    }

    if (!window.confirm(`Delete ${selectedLogIds.length} selected audit logs?`)) {
      return;
    }

    setMessage(null);
    try {
      await Promise.all(selectedLogIds.map((logId) => adminService.deleteAuditLog(logId)));
      await loadLogs({
        action: logAction || undefined,
        status: logStatus || undefined,
        limit: logLimit || 50
      });
      setSelectedLogIds([]);
      setMessage({ type: 'success', text: 'Selected audit logs deleted.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to delete selected logs.'
      });
    }
  };

  const handleDeleteAllLogs = async () => {
    if (!logAction && !logStatus) {
      setMessage({ type: 'error', text: 'Select a log action or status before deleting logs.' });
      return;
    }

    if (!window.confirm('Delete all audit logs matching the selected filters?')) {
      return;
    }

    setMessage(null);
    try {
      await adminService.deleteAuditLogs({
        action: logAction || undefined,
        status: logStatus || undefined
      });
      await loadLogs({
        action: logAction || undefined,
        status: logStatus || undefined,
        limit: logLimit || 50
      });
      setSelectedLogIds([]);
      setMessage({ type: 'success', text: 'Audit logs deleted.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete audit logs.'
      });
    }
  };

  const handleLogFilter = (e) => {
    e.preventDefault();
    const params = {};
    if (logAction) params.action = logAction;
    if (logStatus) params.status = logStatus;
    params.limit = logLimit || 50;
    loadLogs(params);
    setSelectedLogIds([]);
  };


  const toggleSelectOne = (userId, checked) => {
    setSelectedIds((prev) => {
      if (checked) {
        return [...new Set([...prev, userId])];
      }
      return prev.filter((id) => id !== userId);
    });
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one user.' });
      return;
    }

    const confirmText = action === 'delete'
      ? 'Soft delete selected users?'
      : `Apply ${action} to selected users?`;

    if (!window.confirm(confirmText)) {
      return;
    }

    setMessage(null);
    try {
      await Promise.all(selectedIds.map((userId) => {
        switch (action) {
          case 'block':
            return adminService.blockUser(userId);
          case 'unblock':
            return adminService.unblockUser(userId);
          case 'suspend':
            return adminService.suspendUser(userId);
          case 'reactivate':
            return adminService.reactivateUser(userId);
          case 'verify':
            return adminService.verifyUser(userId);
          case 'unverify':
            return adminService.unverifyUser(userId);
          case 'graduate':
            return adminService.graduateUser(userId);
          case 'delete':
            return adminService.deleteUser(userId, false);
          default:
            return Promise.resolve();
        }
      }));
      await loadUsers(filteredParams);
      setSelectedIds([]);
      setMessage({ type: 'success', text: 'Bulk action completed.' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Bulk action failed.' });
    }
  };

  const handleAction = async (userId, action, hard = false) => {
    setMessage(null);
    try {
      switch (action) {
        case 'block':
          await adminService.blockUser(userId);
          break;
        case 'unblock':
          await adminService.unblockUser(userId);
          break;
        case 'suspend':
          await adminService.suspendUser(userId);
          break;
        case 'reactivate':
          await adminService.reactivateUser(userId);
          break;
        case 'verify':
          await adminService.verifyUser(userId);
          break;
        case 'unverify':
          await adminService.unverifyUser(userId);
          break;
        case 'graduate':
          await adminService.graduateUser(userId);
          break;
        case 'restore':
          await adminService.restoreUser(userId);
          break;
        case 'delete':
          await adminService.deleteUser(userId, hard);
          break;
        case 'resetPassword':
          await adminService.sendPasswordReset(userId);
          break;
        default:
          break;
      }
      await loadUsers(filteredParams);
      setMessage({
        type: 'success',
        text: action === 'resetPassword'
          ? 'Password reset email sent.'
          : action === 'restore'
            ? 'User restored.'
            : 'Action completed.'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Action failed.'
      });
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  const toggleSelectAllFor = (list, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...list.map((user) => user._id)])]);
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => !list.some((user) => user._id === id)));
  };

  const formatStatusLabel = (user) => {
    if (user.accountStatus === 'inactive' && !user.deletedAt) {
      return 'pending approval';
    }
    return user.accountStatus || (user.isActive ? 'active' : 'inactive');
  };

  const formatStatusVariant = (user) => {
    if (user.accountStatus === 'inactive' && !user.deletedAt) {
      return 'warning';
    }
    return user.isActive ? 'success' : 'neutral';
  };

  const renderUserTable = (title, list) => (
    <Card className="mt-8">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-display font-semibold text-neutral-800">{title}</h3>
        <Badge variant="secondary">{list.length} total</Badge>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-600">
          {selectedIds.length} selected
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => handleBulkAction('block')}>Block</Button>
          <Button size="sm" variant="ghost" onClick={() => handleBulkAction('unblock')}>Unblock</Button>
          <Button size="sm" variant="ghost" onClick={() => handleBulkAction('suspend')}>Suspend</Button>
          <Button size="sm" variant="ghost" onClick={() => handleBulkAction('reactivate')}>Reactivate</Button>
          <Button size="sm" variant="ghost" onClick={() => handleBulkAction('verify')}>Verify</Button>
          <Button size="sm" variant="ghost" onClick={() => handleBulkAction('unverify')}>Unverify</Button>
          <Button size="sm" variant="secondary" onClick={() => handleBulkAction('graduate')}>Graduate</Button>
          <Button size="sm" variant="danger" onClick={() => handleBulkAction('delete')}>Delete</Button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[1400px] text-left text-xs">
          <thead className="border-b border-neutral-200 text-neutral-500">
            <tr>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">
                <input
                  type="checkbox"
                  checked={list.length > 0 && list.every((user) => selectedIds.includes(user._id))}
                  onChange={(e) => toggleSelectAllFor(list, e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
              </th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Name</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Email</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Role</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">License Type</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">License #</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">State</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Expiration</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">City</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">State (Profile)</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Ordination</th>
              <th className="whitespace-nowrap py-1.5 pr-3 text-center font-medium">Status</th>
              <th className="whitespace-nowrap py-1.5 pr-3 text-center font-medium">Verified</th>
              <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Assigned Clinician</th>
              <th className="whitespace-nowrap py-1.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {list.map((user) => (
              <tr key={user._id} className="hover:bg-neutral-50">
                <td className="whitespace-nowrap py-1.5 pr-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(user._id)}
                    onChange={(e) => toggleSelectOne(user._id, e.target.checked)}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 font-medium text-neutral-800">
                  {user.firstName} {user.lastName}
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.email}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.role}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.licenseType || '—'}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.licenseNumber || '—'}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.licenseState || '—'}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">
                  {user.licenseExpiration ? new Date(user.licenseExpiration).toLocaleDateString() : '—'}
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.city || '—'}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">{user.state || '—'}</td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">
                  {user.role === 'clergy'
                    ? `${user.ordinationChurch || '—'}${user.ordinationYear ? ` (${user.ordinationYear})` : ''}`
                    : '—'}
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-center">
                  <Badge variant={formatStatusVariant(user)} size="sm">
                    {formatStatusLabel(user)}
                  </Badge>
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-center">
                  <Badge variant={user.isVerified ? 'success' : 'neutral'} size="sm">
                    {user.isVerified ? 'Yes' : 'No'}
                  </Badge>
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 text-neutral-600">
                  {user.assignedClinician
                    ? `${user.assignedClinician.firstName} ${user.assignedClinician.lastName}`
                    : '—'}
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="primary"
                      onClick={() => navigate(`/admin/users/${user._id}`)}
                    >
                      View
                    </Button>
                    <ActionsDropdown user={user} />
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={15} className="py-4 text-center text-neutral-500">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );

  // Dropdown menu component
  const ActionsDropdown = ({ user }) => {
    const isOpen = openDropdown === user._id;
    
    return (
      <div className="relative" ref={isOpen ? dropdownRef : null}>
        <button
          onClick={() => setOpenDropdown(isOpen ? null : user._id)}
          className="inline-flex items-center gap-1 rounded-soft border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-gentle"
        >
          Actions
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isOpen && (
          <div className="absolute right-0 z-50 mt-1 w-56 rounded-soft border border-neutral-200 bg-white shadow-lg">
            <div className="py-1">
              <button
                onClick={() => {
                  navigate(`/admin/users/${user._id}`);
                  setOpenDropdown(null);
                }}
                className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
              >
                📋 View Details
              </button>
              
              {user.accountStatus === 'deleted' ? (
                <button
                  onClick={() => {
                    handleAction(user._id, 'restore');
                    setOpenDropdown(null);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-success-700 hover:bg-success-50"
                >
                  ♻️ Restore Account
                </button>
              ) : user.accountStatus === 'inactive' && !user.deletedAt ? (
                <button
                  onClick={() => {
                    handleAction(user._id, 'reactivate');
                    setOpenDropdown(null);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-success-700 hover:bg-success-50"
                >
                  ✓ Approve Account
                </button>
              ) : (
                <>
                  {user.accountStatus === 'blocked' ? (
                    <button
                      onClick={() => {
                        handleAction(user._id, 'unblock');
                        setOpenDropdown(null);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      🔓 Unblock
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        handleAction(user._id, 'block');
                        setOpenDropdown(null);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      🚫 Block
                    </button>
                  )}
                  
                  {user.accountStatus === 'suspended' ? (
                    <button
                      onClick={() => {
                        handleAction(user._id, 'reactivate');
                        setOpenDropdown(null);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      ▶️ Reactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        handleAction(user._id, 'suspend');
                        setOpenDropdown(null);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      ⏸️ Suspend
                    </button>
                  )}
                  
                  {user.isVerified ? (
                    <button
                      onClick={() => {
                        handleAction(user._id, 'unverify');
                        setOpenDropdown(null);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      ✗ Unverify
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        handleAction(user._id, 'verify');
                        setOpenDropdown(null);
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      ✓ Verify
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      handleAction(user._id, 'graduate');
                      setOpenDropdown(null);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                  >
                    🎓 Graduate
                  </button>
                  
                  <button
                    onClick={() => {
                      if (window.confirm('Send a password reset email to this user?')) {
                        handleAction(user._id, 'resetPassword');
                      }
                      setOpenDropdown(null);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                  >
                    🔑 Reset Password
                  </button>
                  
                  <div className="border-t border-neutral-200 my-1"></div>
                  
                  <button
                    onClick={() => {
                      const confirmMsg = `Soft delete ${user.email}?\n\n✓ HIPAA Compliant - Record retained for audit.\n\nThis is the recommended deletion method.`;
                      if (window.confirm(confirmMsg)) {
                        handleAction(user._id, 'delete', false);
                      }
                      setOpenDropdown(null);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-orange-700 hover:bg-orange-50"
                  >
                    🗑️ Soft Delete
                  </button>
                  
                  <button
                    onClick={() => {
                      const warnings = [
                        `⚠️ HARD DELETE ${user.email.toUpperCase()}?`,
                        ``,
                        `⚠️ CRITICAL HIPAA WARNING ⚠️`,
                        ``,
                        `This will PERMANENTLY REMOVE ALL DATA:`,
                        `• User account and all credentials`,
                        `• Journal entries, progress, activities`,
                        `• Clinical notes and assessments`,
                        `• Complete treatment history`,
                        ``,
                        `⚠️ HIPAA VIOLATION WARNING:`,
                        `Healthcare records must be retained for 6+ years.`,
                        `This deletion violates record retention requirements.`,
                        ``,
                        user.role === 'admin' ? `⚠️ ADMIN ACCOUNT DELETION: ${user.email}\n` : '',
                        `This action CANNOT be undone.`,
                        ``,
                        `Type DELETE to confirm:`
                      ].join('\n');
                      
                      const userConfirm = window.prompt(warnings);
                      if (userConfirm === 'DELETE') {
                        handleAction(user._id, 'delete', true);
                      } else if (userConfirm !== null) {
                        alert('Confirmation text did not match. Hard delete cancelled.');
                      }
                      setOpenDropdown(null);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-error-700 hover:bg-error-50 font-semibold"
                  >
                    ⚠️ Hard Delete
                  </button>
                  
                  {user.role === 'admin' && (
                    <div className="px-4 py-2 text-xs text-orange-600 font-semibold bg-orange-50">
                      ⚠️ Admin Account
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-[1920px] px-6 py-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Badge variant="primary" size="sm">Admin Console</Badge>
            <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
              {currentView === 'users' ? 'User Management' : currentView === 'announcements' ? 'System Announcements' : currentView === 'emails' ? 'Email Notifications' : 'Platform Invitations'}
            </h2>
            <p className="mt-2 text-neutral-600">
              {currentView === 'users'
                ? 'Block, verify, graduate, or remove users.'
                : currentView === 'announcements'
                ? 'Create and manage platform-wide technical announcements.'
                : currentView === 'emails'
                ? 'Send individual or mass email notifications to users.'
                : 'Invite clinicians and clients to join the platform.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 h-fit">
            <Button
              onClick={() => setCurrentView('users')}
              variant={currentView === 'users' ? 'primary' : 'secondary'}
            >
              Users
            </Button>
            <Button
              onClick={() => setCurrentView('announcements')}
              variant={currentView === 'announcements' ? 'primary' : 'secondary'}
            >
              Announcements
            </Button>
            <Button
              onClick={() => setCurrentView('invitations')}
              variant={currentView === 'invitations' ? 'primary' : 'secondary'}
            >
              Invitations
            </Button>
            <Button
              onClick={() => setCurrentView('emails')}
              variant={currentView === 'emails' ? 'primary' : 'secondary'}
            >
              Emails
            </Button>
            {currentView === 'users' && (
              <>
                <label className="text-sm text-neutral-700">
                  <span className="sr-only">Account type</span>
                  <select
                    value={createAccountType}
                    onChange={(e) => setCreateAccountType(e.target.value)}
                    className="rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="client">Client</option>
                    <option value="clinician">Clinician</option>
                    <option value="clergy">Clergy</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                <Button
                  onClick={() => navigate(`/admin/create-account?type=${createAccountType}`)}
                  variant="primary"
                >
                  + Create Account
                </Button>
                <Button
                  onClick={() => navigate('/admin/tiers')}
                  variant="secondary"
                >
                  Tier Management
                </Button>
                <Button 
                  onClick={() => navigate('/admin/faith-content')}
                  variant="secondary"
                >
                  Faith Content →
                </Button>
              </>
            )}
          </div>
        </div>

        {currentView === 'announcements' ? (
          <SystemAnnouncementManager />
        ) : currentView === 'invitations' ? (
          <AdminInvitationManager />
        ) : currentView === 'emails' ? (
          <Card>
            <h3 className="text-xl font-display font-semibold text-neutral-900 mb-4">Compose Email</h3>

            {emailMessage && (
              <div className={`mb-4 rounded-soft border p-3 text-sm ${
                emailMessage.type === 'success'
                  ? 'border-success-200 bg-success-50 text-success-700'
                  : 'border-error-200 bg-error-50 text-error-700'
              }`}>
                {emailMessage.text}
              </div>
            )}

            <div className="mb-6 flex gap-2">
              <Button
                size="sm"
                variant={emailMode === 'mass' ? 'primary' : 'secondary'}
                onClick={() => { setEmailMode('mass'); setEmailPreview(null); setEmailMessage(null); }}
              >
                Mass Email
              </Button>
              <Button
                size="sm"
                variant={emailMode === 'individual' ? 'primary' : 'secondary'}
                onClick={() => { setEmailMode('individual'); setEmailPreview(null); setEmailMessage(null); }}
              >
                Individual Email
              </Button>
            </div>

            {emailMode === 'mass' ? (
              <div className="mb-6 grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Target Role</label>
                  <select
                    value={emailTargetRole}
                    onChange={(e) => { setEmailTargetRole(e.target.value); setEmailPreview(null); }}
                    className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="all">All Users</option>
                    <option value="client">Clients</option>
                    <option value="clinician">Clinicians</option>
                    <option value="clergy">Clergy</option>
                    <option value="coach">Coaches</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Target Phase</label>
                  <select
                    value={emailTargetPhase}
                    onChange={(e) => { setEmailTargetPhase(e.target.value); setEmailPreview(null); }}
                    className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="all">All Phases</option>
                    <option value="1">Phase 1 - Stabilize</option>
                    <option value="2">Phase 2 - Understand</option>
                    <option value="3">Phase 3 - Rebuild</option>
                    <option value="4">Phase 4 - Sustain</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Account Status</label>
                  <select
                    value={emailTargetStatus}
                    onChange={(e) => { setEmailTargetStatus(e.target.value); setEmailPreview(null); }}
                    className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="active">Active</option>
                    <option value="all">All Statuses</option>
                    <option value="suspended">Suspended</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => { setEmailRecipient(e.target.value); setEmailPreview(null); }}
                  placeholder="user@example.com"
                  className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Subject</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject line"
                maxLength={200}
                className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Message Body <span className="text-neutral-400 font-normal">(HTML supported)</span>
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Write your email message here. You can use basic HTML tags for formatting (e.g., <b>bold</b>, <p>paragraphs</p>, <a href='...'>links</a>)."
                rows={8}
                className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-y"
              />
            </div>

            {emailPreview && (
              <div className="mb-4 rounded-soft border border-neutral-200 bg-neutral-50 p-4">
                <p className="text-sm font-semibold text-neutral-700 mb-2">
                  Recipients: <span className="text-primary-700">{emailPreview.count}</span> user{emailPreview.count !== 1 ? 's' : ''}
                </p>
                {emailPreview.sample.length > 0 && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Sample recipients:</p>
                    <ul className="text-xs text-neutral-600 space-y-0.5">
                      {emailPreview.sample.map((r, i) => (
                        <li key={i}>{r.name || 'Unknown'} ({r.email}) — {r.role}</li>
                      ))}
                    </ul>
                    {emailPreview.count > 5 && (
                      <p className="text-xs text-neutral-400 mt-1">...and {emailPreview.count - 5} more</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                onClick={async () => {
                  setEmailPreviewing(true);
                  setEmailMessage(null);
                  try {
                    const filters = emailMode === 'individual'
                      ? { recipientEmail: emailRecipient }
                      : { targetRole: emailTargetRole, targetPhase: emailTargetPhase, targetStatus: emailTargetStatus };
                    const res = await adminService.previewEmailRecipients(filters);
                    setEmailPreview(res.data.data);
                  } catch (err) {
                    setEmailMessage({ type: 'error', text: 'Failed to preview recipients.' });
                  } finally {
                    setEmailPreviewing(false);
                  }
                }}
                disabled={emailPreviewing || (emailMode === 'individual' && !emailRecipient)}
              >
                {emailPreviewing ? 'Loading...' : 'Preview Recipients'}
              </Button>
              <Button
                onClick={async () => {
                  if (!emailSubject.trim() || !emailBody.trim()) {
                    setEmailMessage({ type: 'error', text: 'Subject and message body are required.' });
                    return;
                  }
                  const recipientCount = emailPreview?.count || 0;
                  const confirmMsg = emailMode === 'individual'
                    ? `Send email to ${emailRecipient}?`
                    : `Send email to ${recipientCount} user${recipientCount !== 1 ? 's' : ''}? This cannot be undone.`;
                  if (!window.confirm(confirmMsg)) return;

                  setEmailSending(true);
                  setEmailMessage(null);
                  try {
                    const payload = {
                      subject: emailSubject,
                      htmlBody: emailBody,
                      ...(emailMode === 'individual'
                        ? { recipientEmail: emailRecipient }
                        : { targetRole: emailTargetRole, targetPhase: emailTargetPhase, targetStatus: emailTargetStatus }
                      )
                    };
                    const res = await adminService.sendEmail(payload);
                    setEmailMessage({ type: 'success', text: res.data.message });
                    setEmailSubject('');
                    setEmailBody('');
                    setEmailRecipient('');
                    setEmailPreview(null);
                  } catch (err) {
                    setEmailMessage({ type: 'error', text: err.response?.data?.message || 'Failed to send email.' });
                  } finally {
                    setEmailSending(false);
                  }
                }}
                disabled={emailSending || !emailSubject.trim() || !emailBody.trim() || (emailMode === 'individual' && !emailRecipient)}
              >
                {emailSending ? 'Sending...' : emailMode === 'individual' ? 'Send Email' : 'Send to All Recipients'}
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {message && (
              <div
                className={`mb-6 rounded-soft border p-3 text-sm ${
                  message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <Card>
          <form onSubmit={handleFilter} className="grid gap-4 md:grid-cols-4">
            <Input
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, username"
              fullWidth
            />
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">All roles</option>
                <option value="client">Client</option>
                <option value="clinician">Clinician</option>
                <option value="clergy">Clergy</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="blocked">Blocked</option>
                <option value="inactive">Pending Approval</option>
                <option value="deleted">Deleted</option>
              </select>
            </label>
            <div className="flex items-end gap-2">
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setStatus('inactive');
                  loadUsers({
                    search: search || undefined,
                    role: role || undefined,
                    status: 'inactive',
                    includeDeleted: true
                  });
                }}
              >
                Pending Only
              </Button>
              <Button type="submit" className="w-full">Apply Filters</Button>
            </div>
          </form>
        </Card>

        {renderUserTable('All Users', users)}

        <Card className="mt-8">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-neutral-800">Audit Logs</h3>
            <Badge variant="secondary">{logs.length} entries</Badge>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-neutral-200">
            <p className="text-sm text-neutral-600">
              {selectedLogIds.length} selected
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedLogIds.length > 0 && (
                <Button size="sm" variant="danger" onClick={handleDeleteSelectedLogs}>
                  Delete Selected ({selectedLogIds.length})
                </Button>
              )}
              <Button size="sm" variant="danger" onClick={handleDeleteAllLogs}>Delete All</Button>
            </div>
          </div>

          <form onSubmit={handleLogFilter} className="mt-4 grid gap-4 md:grid-cols-4">
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Action</span>
              <input
                value={logAction}
                onChange={(e) => setLogAction(e.target.value)}
                placeholder="e.g., USER_BLOCK"
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </label>
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Status</span>
              <select
                value={logStatus}
                onChange={(e) => setLogStatus(e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">All statuses</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILURE">FAILURE</option>
                <option value="WARNING">WARNING</option>
              </select>
            </label>
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Limit</span>
              <select
                value={logLimit}
                onChange={(e) => setLogLimit(e.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </label>
            <div className="flex items-end">
              <Button type="submit" className="w-full">Apply</Button>
            </div>
          </form>

          <div className="mt-4 overflow-x-auto">
            {logLoading ? (
              <p className="py-6 text-center text-neutral-500">Loading audit logs...</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-neutral-200 text-neutral-500">
                  <tr>
                    <th className="py-3 pr-4 font-medium">
                      <input
                        type="checkbox"
                        checked={selectedLogIds.length === logs.length && logs.length > 0}
                        onChange={(e) => toggleSelectAllLogs(e.target.checked)}
                        className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th className="py-3 pr-4 font-medium">Timestamp</th>
                    <th className="py-3 pr-4 font-medium">User</th>
                    <th className="py-3 pr-4 font-medium">Action</th>
                    <th className="py-3 pr-4 font-medium">Resource</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                    <th className="py-3 text-right font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-neutral-50">
                      <td className="py-3 pr-4">
                        <input
                          type="checkbox"
                          checked={selectedLogIds.includes(log._id)}
                          onChange={(e) => toggleSelectLog(log._id, e.target.checked)}
                          className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="py-3 pr-4 text-neutral-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 pr-4 text-neutral-600">
                        {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'System'}
                      </td>
                      <td className="py-3 pr-4 text-neutral-600">{log.action}</td>
                      <td className="py-3 pr-4 text-neutral-600">{log.resource}</td>
                      <td className="py-3 pr-4">
                        <Badge variant={log.status === 'SUCCESS' ? 'success' : log.status === 'FAILURE' ? 'error' : 'neutral'} size="sm">
                          {log.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-right">
                        <Button 
                          size="sm" 
                          variant="danger"
                          onClick={() => handleDeleteLog(log._id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-neutral-500">
                        No audit logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Card>
          </>
        )}
      </main>

      <SOSButton variant="floating" />
    </>
  );
};

export default AdminDashboard;
